import {ImageIcon} from '@primer/octicons-react'
import {Dialog, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {useEffect, useState} from 'react'
import type {CustomTokenProps, ImageReference} from '@github-ui/copilot-chat-types'
import styles from './ImageReferenceToken.module.css'

/**
 * A specialized reference token that displays a thumbnail preview for image references.
 * Opens a dialog with the full image when clicked.
 * Used as a custom TokenComponent for markdown-detected images.
 */
export function ImageReferenceToken({reference, size, fullWidth, tokenRef}: CustomTokenProps) {
  const imageRef = reference as ImageReference
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(imageRef.imageUrl)
  // Resolve URL from attachment.url() if imageUrl is not available
  useEffect(() => {
    const attachment = imageRef.attachment
    if (!imageRef.imageUrl && attachment?.url) {
      const resolveUrl = async () => {
        try {
          const url = await attachment.url()
          setResolvedUrl(url)
        } catch {
          setError(true)
        }
      }
      void resolveUrl()
    }
  }, [imageRef.imageUrl, imageRef.attachment])

  const handleClick = () => {
    setDialogOpen(true)
  }

  return (
    <>
      <button
        ref={tokenRef as React.RefCallback<HTMLButtonElement>}
        type="button"
        className={clsx(styles.imageReferenceToken, size === 'small' && styles.small, fullWidth && styles.fullWidth)}
        onClick={handleClick}
        aria-label={`Image: ${imageRef.name}`}
      >
        <div className={styles.thumbnailContainer}>
          {!loaded && !error && (
            <div className={styles.loadingState}>
              <Spinner size="small" />
            </div>
          )}
          {error ? (
            <div className={styles.errorState}>
              <ImageIcon size={16} />
            </div>
          ) : (
            resolvedUrl && (
              <img
                src={resolvedUrl}
                alt={imageRef.name}
                onLoad={() => setLoaded(true)}
                onError={() => setError(true)}
                className={clsx(styles.thumbnail, !loaded && styles.hidden)}
              />
            )
          )}
        </div>
        <span className={styles.name}>{imageRef.name}</span>
      </button>

      {dialogOpen && resolvedUrl && (
        <Dialog onClose={() => setDialogOpen(false)} title={imageRef.name} width="xlarge">
          <div className={styles.dialogImageContainer}>
            <img src={resolvedUrl} alt={imageRef.name} className={styles.dialogImage} />
          </div>
        </Dialog>
      )}
    </>
  )
}
